import { Fragment, ReactElement, ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Button,
  Field,
  Heading,
  Label,
  Link,
  Paragraph,
  Skeleton,
  Tabs,
  Tag,
  Textarea,
  ToggleGroup,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import {
  ExclamationmarkTriangleIcon,
  FileTextIcon,
  PersonGroupIcon,
  ShieldIcon,
  TasklistIcon,
} from '@navikt/aksel-icons';
import type { SafetyRepOverview, SafetyRepPending } from '../../api/hms';
import type {
  RepTaskDeviation,
  RoundToDo,
  TaskRound,
} from '../../api/safetyRounds';
import type { SafetyRepTask, SafetyRepWork } from '../../api/safetyRepWork';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { Routes } from '../../enums/Routes';
import {
  useApproveHmsAsSafetyRepMutation,
  useHmsSafetyRepQuery,
  useReturnHmsAsSafetyRepMutation,
} from '../../hooks/queries/hms/useHmsQueries';
import {
  useSafetyRepWorkQuery,
  useUndoDocumentationAnswerMutation,
} from '../../hooks/queries/safetyRep/useSafetyRepWork';
import { useDeviationActionMutation } from '../../hooks/queries/safetyRounds/useDeviationQueries';
import { useMyTasksQuery } from '../../hooks/queries/safetyRounds/useTaskQueries';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { deviationPath } from '../SafetyRounds/Deviations/deviationView';
import { StartRound } from '../SafetyRounds/Plans/PlansPage';
import { longDate, planPath } from '../SafetyRounds/Plans/plansView';
import { protocolPath } from '../SafetyRounds/Protocol/protocolView';
import { DocumentationDecision } from './components/DocumentationDecision';
import { VersionDecision } from './components/VersionDecision';
import {
  completedList,
  countsOf,
  deadlineState,
  deadlineText,
  deviationTitle,
  type Filter,
  FILTERS,
  filtered,
  fixedText,
  followingList,
  isUrgent,
  named,
  relativeDay,
  type ToDo,
  toDoList,
  wasSentBack,
} from './repTasksView';
import { taskLink, taskTexts } from './safetyRepWorkView';
import {
  alreadyHandled,
  isOwnVersionError,
  type Decision,
  doneText,
  itemKey,
  type LastDone,
} from './safetyRepView';
import layout from '../HRDashboard/hmsPage.module.css';
import classes from './SafetyRepTasks.module.css';

const K = 'safetyRep.myTasks';

type Tab = 'toDo' | 'following' | 'completed';

/** Today in the user's own time zone, as the deadlines are days */
const todayDay = () => new Date().toLocaleDateString('sv-SE');

/**
 * «Mine oppgaver» for the safety representative: the safety rounds and the HMS work in one list sorted by deadline,
 * the open deviations they follow, and what they have done. The general manager has their own page.
 */
export const SafetyRepTasks = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const { features } = useFeatureFlag();
  const withRounds = features.includes('SAFETY_ROUNDS');
  const work = useSafetyRepWorkQuery(questionLanguageFor(i18n.language));
  const overview = useHmsSafetyRepQuery();
  const rounds = useMyTasksQuery(withRounds);
  const [tab, setTab] = useState<Tab>('toDo');
  // Answered deviations leave at once, from the list and the counts, until the tasks are fetched again
  const [handled, setHandled] = useState<Array<{ key: string; at: number }>>(
    [],
  );
  const fetchedAt = rounds.dataUpdatedAt ?? 0;
  const isHandled = (key: string) =>
    handled.some((entry) => entry.key === key && fetchedAt <= entry.at);
  const today = todayDay();

  const loading =
    work.isLoading || overview.isLoading || (withRounds && rounds.isLoading);
  const failed =
    !loading && (work.error || overview.error || !work.data || !overview.data);
  const repRounds = withRounds ? (rounds.data?.safetyRep ?? null) : null;
  const ready = !loading && !failed && work.data && overview.data;

  const toDo = ready
    ? toDoList(repRounds, work.data ?? null).filter(
        (item) => !isHandled(item.key),
      )
    : [];
  const following = followingList(repRounds);
  const completed = ready
    ? completedList(repRounds, work.data ?? null, t, i18n.language)
    : [];
  const tabs: Tab[] = withRounds
    ? ['toDo', 'following', 'completed']
    : ['toDo', 'completed'];
  const counts: Record<Tab, number> = {
    toDo: toDo.length,
    following: following.length,
    completed: completed.length,
  };

  const content = (panel: Tab) => {
    if (loading)
      return (
        <div aria-busy="true">
          <span className="ds-sr-only">{t(`${K}.loading`)}</span>
          <Skeleton variant="rectangle" width="100%" height={280} />
        </div>
      );
    if (!ready)
      return (
        <Alert data-color="danger">
          <Paragraph>{t(`${K}.error`)}</Paragraph>
        </Alert>
      );
    const roundsFailed = withRounds && (rounds.error || !rounds.data) && (
      <Alert data-color="warning" data-size="sm">
        <Paragraph data-size="sm">{t(`${K}.roundsError`)}</Paragraph>
      </Alert>
    );
    return (
      <div className={classes.panel}>
        {roundsFailed}
        {panel === 'toDo' && (
          <ToDoPanel
            items={toDo}
            work={work.data!}
            overview={overview.data!}
            withRounds={withRounds}
            today={today}
            onHandled={(key) =>
              setHandled((entries) => [...entries, { key, at: fetchedAt }])
            }
          />
        )}
        {panel === 'following' && (
          <FollowingPanel deviations={following} today={today} />
        )}
        {panel === 'completed' && <CompletedPanel items={completed} />}
      </div>
    );
  };

  return (
    <div className={`${layout.page} ${classes.page}`} data-size="md">
      <Tabs
        value={tab}
        onChange={(value) => setTab(value as Tab)}
        className={classes.tabs}
        data-size="sm"
      >
        <header className={classes.header}>
          <div className={classes.headerInner}>
            <div className={classes.titles}>
              <span className={layout.eyebrow}>{t(`${K}.eyebrow`)}</span>
              <Heading level={1} data-size="sm" className={classes.title}>
                {t(`${K}.title`)}
              </Heading>
            </div>
            <Link asChild data-size="sm">
              <RouterLink
                to={
                  withRounds
                    ? Routes.SafetyRoundNotifications
                    : Routes.SafetyRepOverview
                }
              >
                {t(`${K}.emails`)}
              </RouterLink>
            </Link>
          </div>
          <div className={classes.tabsScroll}>
            <Tabs.List className={classes.tabList} aria-label={t(`${K}.tabs`)}>
              {tabs.map((entry) => (
                <Tabs.Tab key={entry} value={entry}>
                  {t(`${K}.tab.${entry}`)}
                  {ready && (
                    <span
                      className={classes.count}
                      data-kind={entry}
                      data-empty={counts[entry] === 0 || undefined}
                    >
                      <span aria-hidden="true">{counts[entry]}</span>
                      <span className="ds-sr-only">
                        ({t(`${K}.tabCount.${entry}`, { count: counts[entry] })}
                        )
                      </span>
                    </span>
                  )}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </div>
        </header>
        <div className={classes.content}>
          {tabs.map((entry) => (
            <Tabs.Panel key={entry} value={entry}>
              {tab === entry && content(entry)}
            </Tabs.Panel>
          ))}
        </div>
      </Tabs>
    </div>
  );
};

/** What was just done stays in view after the task has left the list */
type Notice =
  | { kind: 'hms'; done: LastDone }
  | { kind: 'deviation'; text: string };

const ToDoPanel = ({
  items,
  work,
  overview,
  withRounds,
  today,
  onHandled,
}: {
  items: ToDo[];
  work: SafetyRepWork;
  overview: SafetyRepOverview;
  withRounds: boolean;
  today: string;
  onHandled: (key: string) => void;
}): ReactElement => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>('all');
  const [notice, setNotice] = useState<Notice | null>(null);
  const approve = useApproveHmsAsSafetyRepMutation();
  const giveBack = useReturnHmsAsSafetyRepMutation();
  const [deciding, setDeciding] = useState<string | null>(null);
  const [failure, setFailure] = useState<{
    key: string;
    error: 'alreadyHandled' | 'ownVersion' | 'failed';
  } | null>(null);

  const decide = (
    item: SafetyRepPending,
    decision: Decision,
    comment: string,
  ) => {
    const key = itemKey(item);
    setDeciding(key);
    setFailure(null);
    const callbacks = {
      onSuccess: () =>
        setNotice({
          kind: 'hms',
          done: { kind: 'version', subject: item.subject, decision, comment },
        }),
      onError: (cause: unknown) =>
        setFailure({
          key,
          error: alreadyHandled(cause) ? 'alreadyHandled' : isOwnVersionError(cause) ? 'ownVersion' : 'failed',
        }),
    };
    if (decision === 'approve')
      approve.mutate({ subject: item.subject, id: item.id }, callbacks);
    else
      giveBack.mutate(
        { subject: item.subject, id: item.id, comment },
        callbacks,
      );
  };
  const versionDecision = {
    decide,
    deciding: (item: SafetyRepPending) =>
      (approve.isPending || giveBack.isPending) && deciding === itemKey(item),
    error: (item: SafetyRepPending) =>
      failure?.key === itemKey(item) ? failure.error : null,
  };

  const counts = countsOf(items);
  const shown = filtered(items, filter);
  const filters = withRounds
    ? FILTERS
    : FILTERS.filter((entry) => entry === 'all' || entry === 'documents');

  const onDeviationDone = (key: string, text: string) => {
    onHandled(key);
    setNotice({ kind: 'deviation', text });
  };

  return (
    <>
      <div role="status" aria-live="polite">
        {notice?.kind === 'hms' && (
          <HmsDone
            done={notice.done}
            work={work}
            onUndone={() => setNotice(null)}
          />
        )}
        {notice?.kind === 'deviation' && (
          <Alert data-color="success" data-size="sm">
            <Paragraph data-size="sm">{notice.text}</Paragraph>
          </Alert>
        )}
      </div>
      <section aria-label={t(`${K}.tab.toDo`)} className={classes.card}>
        <div className={classes.cardHead}>
          <ToggleGroup
            data-toggle-group={t(`${K}.filterLabel`)}
            value={filter}
            onChange={(value) => setFilter(value as Filter)}
            data-size="sm"
            className={classes.filter}
          >
            {filters.map((entry) => (
              <ToggleGroup.Item key={entry} value={entry}>
                {t(`${K}.filterCount`, {
                  label: t(`${K}.filter.${entry}`),
                  count: counts[entry],
                })}
              </ToggleGroup.Item>
            ))}
          </ToggleGroup>
          {shown.length > 1 && (
            <span className={classes.subtle}>{t(`${K}.sorted`)}</span>
          )}
        </div>
        {shown.length === 0 ? (
          <Paragraph
            data-size="sm"
            className={`${classes.empty} ${layout.subtle}`}
          >
            {t(`${K}.empty.${filter}`)}
          </Paragraph>
        ) : (
          <ul className={classes.rows}>
            {shown.map((item) => (
              <ToDoRow
                key={item.key}
                item={item}
                today={today}
                work={work}
                overview={overview}
                urgent={isUrgent(item, today)}
                versionDecision={versionDecision}
                onHmsDone={(done) => setNotice({ kind: 'hms', done })}
                onDeviationDone={onDeviationDone}
              />
            ))}
          </ul>
        )}
      </section>
    </>
  );
};

/** What the safety representative just answered in the HMS work. A return can be undone until the work is archived, a signature is final. */
const HmsDone = ({
  done,
  work,
  onUndone,
}: {
  done: LastDone;
  work: SafetyRepWork;
  onUndone: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const undo = useUndoDocumentationAnswerMutation();
  const undoable =
    done.kind === 'documentation' &&
    done.decision === 'return' &&
    work.documentation.some(
      (item) => item.surveyId === done.surveyId && item.status === 'RETURNED' && item.canUndo,
    );
  return (
    <Alert data-color="success" data-size="sm">
      <div className={classes.notice}>
        <Paragraph data-size="sm">{doneText(done, t)}</Paragraph>
        {undoable && (
          <Button
            variant="tertiary"
            data-size="sm"
            loading={undo.isPending}
            onClick={() => undo.mutate(done.surveyId, { onSuccess: onUndone })}
          >
            {t('safetyRep.work.documentation.undo')}
          </Button>
        )}
      </div>
    </Alert>
  );
};

type VersionDecisionProps = {
  decide: (item: SafetyRepPending, decision: Decision, comment: string) => void;
  deciding: (item: SafetyRepPending) => boolean;
  error: (item: SafetyRepPending) => 'alreadyHandled' | 'ownVersion' | 'failed' | null;
};

const ToDoRow = ({
  item,
  today,
  work,
  overview,
  urgent,
  versionDecision,
  onHmsDone,
  onDeviationDone,
}: {
  item: ToDo;
  today: string;
  work: SafetyRepWork;
  overview: SafetyRepOverview;
  urgent: boolean;
  versionDecision: VersionDecisionProps;
  onHmsDone: (done: LastDone) => void;
  onDeviationDone: (key: string, text: string) => void;
}): ReactElement => {
  const variant = urgent ? 'primary' : 'secondary';
  switch (item.kind) {
    case 'round':
      return <RoundRow round={item.round} today={today} variant={variant} />;
    case 'protocol':
      return <ProtocolRow round={item.round} today={today} variant={variant} />;
    case 'deviation':
      return (
        <DeviationRow
          deviation={item.deviation}
          today={today}
          onDone={(text) => onDeviationDone(item.key, text)}
        />
      );
    case 'hms':
      return (
        <HmsRow
          task={item.task}
          today={today}
          variant={variant}
          work={work}
          overview={overview}
          versionDecision={versionDecision}
          onDone={onHmsDone}
        />
      );
  }
};

/** One row: an icon, the title with what it is and its deadline, the actions, and what opens under it */
const Row = ({
  icon,
  title,
  meta,
  deadline,
  today,
  actions,
  more,
  moreId,
}: {
  icon: ReactNode;
  title: ReactNode;
  meta: string[];
  deadline?: string | null;
  today: string;
  actions: ReactNode;
  more?: ReactNode;
  moreId?: string;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <li className={classes.row}>
      <div className={classes.rowMain}>
        <span className={classes.icon} aria-hidden="true">
          {icon}
        </span>
        <div className={classes.rowText}>
          <span className={classes.rowTitle}>{title}</span>
          <span className={`${classes.subtle} ${classes.meta}`}>
            <span>{meta.filter(Boolean).join(' · ')}</span>
            {deadline && (
              <span
                className={classes.deadline}
                data-state={deadlineState(deadline, today)}
              >
                {deadlineText(deadline, today, t, i18n.language)}
              </span>
            )}
          </span>
        </div>
        <div className={classes.actions}>{actions}</div>
      </div>
      {more && (
        <div id={moreId} className={classes.more}>
          {more}
        </div>
      )}
    </li>
  );
};

/** «Detaljer», which opens and closes what is under the row */
const DetailsToggle = ({
  open,
  controls,
  title,
  onClick,
}: {
  open: boolean;
  controls: string;
  title: string;
  onClick: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const text = open ? t(`${K}.hide`) : t(`${K}.details`);
  return (
    <Button
      data-size="sm"
      variant="tertiary"
      aria-expanded={open}
      aria-controls={controls}
      aria-label={named(text, title)}
      onClick={onClick}
    >
      {text}
    </Button>
  );
};

const Facts = ({
  facts,
}: {
  facts: Array<[string, ReactNode]>;
}): ReactElement => (
  <dl className={classes.facts}>
    {facts.map(([term, value]) => (
      <Fragment key={term}>
        <dt>{term}</dt>
        <dd>{value}</dd>
      </Fragment>
    ))}
  </dl>
);

const RoundRow = ({
  round,
  today,
  variant,
}: {
  round: RoundToDo;
  today: string;
  variant: 'primary' | 'secondary';
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const moreId = useId();
  return (
    <Row
      icon={<TasklistIcon />}
      title={round.planName}
      meta={[
        t(`${K}.kind.round`),
        round.frequency
          ? t(`safetyRounds.plans.frequency.${round.frequency}`)
          : '',
        round.area,
      ]}
      deadline={round.nextDate}
      today={today}
      actions={
        <>
          <StartRound
            plan={{ id: round.planId, currentRoundId: round.roundId, status: round.status, canStart: true, startsFrom: round.nextDate }}
            variant={variant}
            name={round.planName}
          />
          <DetailsToggle
            open={open}
            controls={moreId}
            title={round.planName}
            onClick={() => setOpen(!open)}
          />
        </>
      }
      moreId={moreId}
      more={
        open && (
          <>
            <Facts
              facts={[
                [t(`${K}.area`), round.area],
                [t(`${K}.planned`), longDate(round.nextDate, i18n.language)],
              ]}
            />
            <Link asChild data-size="sm" className={layout.startAligned}>
              <RouterLink to={planPath(round.planId)}>
                {t(`${K}.openPlan`)}
              </RouterLink>
            </Link>
          </>
        )
      }
    />
  );
};

/** A protocol the general manager sent back, with what they asked for */
const ProtocolRow = ({
  round,
  today,
  variant,
}: {
  round: TaskRound;
  today: string;
  variant: 'primary' | 'secondary';
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const moreId = useId();
  const request = round.changeRequest;
  return (
    <Row
      icon={<FileTextIcon />}
      title={round.planName}
      meta={[
        t(`${K}.kind.protocol`),
        request
          ? t(`${K}.sentBack`, {
              when: relativeDay(request.at, today, t, i18n.language),
            })
          : t(`${K}.sentBackNoDate`),
      ]}
      today={today}
      actions={
        <>
          <Button asChild data-size="sm" variant={variant}>
            <RouterLink
              to={protocolPath(round.id)}
              aria-label={named(t(`${K}.correct`), round.planName)}
            >
              {t(`${K}.correct`)}
            </RouterLink>
          </Button>
          <DetailsToggle
            open={open}
            controls={moreId}
            title={round.planName}
            onClick={() => setOpen(!open)}
          />
        </>
      }
      moreId={moreId}
      more={
        open && (
          <>
            <div>
              <span className="ds-label" data-size="sm">
                {t(`${K}.managerComment`)}
              </span>
              <Paragraph data-size="sm" className={classes.quote}>
                {request?.comment || t(`${K}.noComment`)}
              </Paragraph>
            </div>
            <Link asChild data-size="sm" className={layout.startAligned}>
              <RouterLink to={protocolPath(round.id)}>
                {t(`${K}.openProtocol`)}
              </RouterLink>
            </Link>
          </>
        )
      }
    />
  );
};

/**
 * A fixed deviation to check. «Godkjenn» closes it at once, unless the safety representative was responsible for it
 * themselves: then they confirm, as the log says so. «Åpne igjen» sends it back with what is missing.
 */
const DeviationRow = ({
  deviation,
  today,
  onDone,
}: {
  deviation: RepTaskDeviation;
  today: string;
  onDone: (text: string) => void;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<'details' | 'reopen' | 'ownFix' | null>(
    null,
  );
  const [comment, setComment] = useState('');
  const [tried, setTried] = useState(false);
  const act = useDeviationActionMutation(deviation.id);
  const moreId = useId();
  const commentId = useId();
  const title = deviationTitle(deviation);
  const missing = tried && !comment.trim();

  const confirm = () =>
    act.mutate(
      { action: 'confirmFixed', input: {} },
      { onSuccess: () => onDone(t(`${K}.approved`, { title })) },
    );
  const reopen = () => {
    setTried(true);
    if (!comment.trim()) return;
    act.mutate(
      { action: 'sendBack', input: { comment: comment.trim() } },
      { onSuccess: () => onDone(t(`${K}.reopened`, { title })) },
    );
  };
  const toggle = (next: typeof mode) => {
    setMode(mode === next ? null : next);
    setTried(false);
    act.reset();
  };

  return (
    <Row
      icon={<ShieldIcon />}
      title={title}
      meta={[
        t(`${K}.kind.deviation`),
        fixedText(deviation, today, t, i18n.language),
      ]}
      today={today}
      actions={
        <>
          <Button
            data-size="sm"
            aria-label={named(t(`${K}.approve`), title)}
            aria-expanded={deviation.ownFix ? mode === 'ownFix' : undefined}
            aria-controls={deviation.ownFix ? moreId : undefined}
            aria-busy={(act.isPending && mode !== 'reopen') || undefined}
            onClick={() => (deviation.ownFix ? toggle('ownFix') : confirm())}
          >
            {t(`${K}.approve`)}
          </Button>
          <Button
            data-size="sm"
            variant="secondary"
            aria-label={named(t(`${K}.reopen`), title)}
            aria-expanded={mode === 'reopen'}
            aria-controls={moreId}
            onClick={() => toggle('reopen')}
          >
            {t(`${K}.reopen`)}
          </Button>
          <DetailsToggle
            open={mode === 'details'}
            controls={moreId}
            title={title}
            onClick={() => toggle('details')}
          />
        </>
      }
      moreId={moreId}
      more={
        (mode !== null || act.isError) && (
          <>
            {mode === 'details' && (
              <>
                <div>
                  <span className="ds-label" data-size="sm">
                    {t(`${K}.whatWasDone`)}
                  </span>
                  <Paragraph data-size="sm" className={classes.quote}>
                    {deviation.actionTaken || t(`${K}.nothingWritten`)}
                  </Paragraph>
                </div>
                <Facts
                  facts={[
                    [
                      t(`${K}.responsible`),
                      deviation.responsibleEmail ?? t(`${K}.noResponsible`),
                    ],
                    [
                      t(`${K}.deviationDue`),
                      longDate(deviation.dueDate, i18n.language),
                    ],
                    [t(`${K}.plan`), deviation.planName],
                  ]}
                />
                {deviation.ownFix && (
                  <Paragraph data-size="sm">
                    {t('safetyRounds.deviation.confirmingOwnFix')}
                  </Paragraph>
                )}
                <Link asChild data-size="sm" className={layout.startAligned}>
                  <RouterLink to={deviationPath(deviation.id)}>
                    {t(`${K}.openDeviation`)}
                  </RouterLink>
                </Link>
              </>
            )}
            {mode === 'ownFix' && (
              <>
                <Heading level={3} data-size="2xs">
                  {t(`${K}.ownFixTitle`)}
                </Heading>
                <Paragraph data-size="sm">
                  {t('safetyRounds.deviation.confirmingOwnFix')}
                </Paragraph>
                <div className={classes.formActions}>
                  <Button
                    data-size="sm"
                    aria-busy={act.isPending || undefined}
                    onClick={confirm}
                  >
                    {t(`${K}.ownFixSubmit`)}
                  </Button>
                  <Button
                    data-size="sm"
                    variant="secondary"
                    onClick={() => toggle(null)}
                  >
                    {t(`${K}.cancel`)}
                  </Button>
                </div>
              </>
            )}
            {mode === 'reopen' && (
              <>
                <Field>
                  <Label htmlFor={commentId}>{t(`${K}.reopenLabel`)}</Label>
                  <Paragraph data-size="sm" id={`${commentId}-to`}>
                    {deviation.responsibleEmail
                      ? t(`${K}.reopenHint`, {
                          who: deviation.responsibleEmail,
                        })
                      : t(`${K}.reopenHintUnknown`)}
                  </Paragraph>
                  <Textarea
                    id={commentId}
                    rows={3}
                    autoFocus
                    value={comment}
                    aria-describedby={`${commentId}-to`}
                    aria-invalid={missing || undefined}
                    onChange={(event) => setComment(event.target.value)}
                  />
                  {missing && (
                    <ValidationMessage>
                      {t(`${K}.reopenError`)}
                    </ValidationMessage>
                  )}
                </Field>
                <div className={classes.formActions}>
                  <Button
                    data-size="sm"
                    aria-busy={act.isPending || undefined}
                    onClick={reopen}
                  >
                    {t(`${K}.reopenSubmit`)}
                  </Button>
                  <Button
                    data-size="sm"
                    variant="secondary"
                    onClick={() => toggle(null)}
                  >
                    {t(`${K}.cancel`)}
                  </Button>
                </div>
              </>
            )}
            {act.isError && (
              <Alert data-color="danger" data-size="sm">
                <Paragraph data-size="sm">{t(`${K}.failed`)}</Paragraph>
              </Alert>
            )}
          </>
        )
      }
    />
  );
};

const HMS_ICONS: Record<SafetyRepTask['kind'], ReactNode> = {
  sign: <FileTextIcon />,
  approveVersion: <PersonGroupIcon />,
  reviewPlan: <FileTextIcon />,
  assessRisk: <ExclamationmarkTriangleIcon />,
  highRisk: <ExclamationmarkTriangleIcon />,
};

/**
 * A task in the HMS work. Documentation and versions of the setup are read and answered under the row. The rest is
 * done on the page of the survey.
 */
const HmsRow = ({
  task,
  today,
  variant,
  work,
  overview,
  versionDecision,
  onDone,
}: {
  task: SafetyRepTask;
  today: string;
  variant: 'primary' | 'secondary';
  work: SafetyRepWork;
  overview: SafetyRepOverview;
  versionDecision: VersionDecisionProps;
  onDone: (done: LastDone) => void;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const moreId = useId();
  const texts = taskTexts(task, t, i18n.language);
  const documentation =
    task.kind === 'sign'
      ? work.documentation.find(
          (item) =>
            item.surveyId === task.surveyId && item.status === 'REQUESTED',
        )
      : undefined;
  const version = task.version
    ? overview.pending.find(
        (item) =>
          item.subject === task.version?.subject && item.id === task.version.id,
      )
    : undefined;
  const title = documentation
    ? t(`${K}.documentationTitle`, { team: documentation.team })
    : task.version
      ? t(`safetyRep.subjects.${task.version.subject}`, {
          version: task.version.version,
        })
      : texts.title;
  const from = t(`${K}.from`, { from: task.team ?? t(`${K}.hr`) });
  const answeredHere = Boolean(documentation || version);
  const action = documentation
    ? t(`${K}.seeAndSign`)
    : version
      ? t(`${K}.seeAndApprove`)
      : texts.action;

  return (
    <Row
      icon={HMS_ICONS[task.kind]}
      title={title}
      meta={[
        texts.kind,
        from,
        // Without a deadline, how long it has waited
        task.due
          ? ''
          : t(`${K}.sent`, {
              when: relativeDay(task.since, today, t, i18n.language),
            }),
      ]}
      deadline={task.due}
      today={today}
      actions={
        answeredHere ? (
          <Button
            data-size="sm"
            variant={open ? 'secondary' : variant}
            aria-label={named(action, title)}
            aria-expanded={open}
            aria-controls={moreId}
            onClick={() => setOpen(!open)}
          >
            {action}
          </Button>
        ) : (
          <>
            <Button asChild data-size="sm" variant={variant}>
              <RouterLink to={taskLink(task)} aria-label={named(action, title)}>
                {action}
              </RouterLink>
            </Button>
            <DetailsToggle
              open={open}
              controls={moreId}
              title={title}
              onClick={() => setOpen(!open)}
            />
          </>
        )
      }
      moreId={moreId}
      more={
        open &&
        (documentation ? (
          <DocumentationDecision
            item={documentation}
            onDone={(decision, comment) =>
              onDone({
                kind: 'documentation',
                surveyId: documentation.surveyId,
                decision,
                comment,
              })
            }
          />
        ) : version ? (
          <VersionDecision
            item={version}
            onDecide={(decision, comment) =>
              versionDecision.decide(version, decision, comment)
            }
            deciding={versionDecision.deciding(version)}
            error={versionDecision.error(version)}
          />
        ) : (
          <Paragraph data-size="sm">{texts.text}</Paragraph>
        ))
      }
    />
  );
};

/** The open deviations of the safety representative's rounds, waiting on someone else */
const FollowingPanel = ({
  deviations,
  today,
}: {
  deviations: RepTaskDeviation[];
  today: string;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <section aria-label={t(`${K}.tab.following`)} className={classes.card}>
      <Paragraph data-size="sm" className={classes.intro}>
        {t(`${K}.followingIntro`)}
      </Paragraph>
      {deviations.length === 0 ? (
        <Paragraph
          data-size="sm"
          className={`${classes.empty} ${layout.subtle}`}
        >
          {t(`${K}.empty.following`)}
        </Paragraph>
      ) : (
        <ul className={classes.rows}>
          {deviations.map((deviation) => (
            <li key={deviation.id} className={classes.row}>
              <div className={classes.rowMain} data-icon="false">
                <div className={classes.rowText}>
                  <Link asChild data-size="sm" className={classes.rowLink}>
                    <RouterLink to={deviationPath(deviation.id)}>
                      {deviationTitle(deviation)}
                    </RouterLink>
                  </Link>
                  <span className={classes.subtle}>
                    {[
                      deviation.planName,
                      deviation.responsibleEmail ?? t(`${K}.noResponsible`),
                    ].join(' · ')}
                  </span>
                </div>
                <div className={classes.side}>
                  <Tag
                    data-size="sm"
                    data-color={wasSentBack(deviation) ? 'warning' : 'neutral'}
                  >
                    {wasSentBack(deviation)
                      ? t(`${K}.sentBackTag`)
                      : t(
                          `safetyRounds.protocol.deviationStatus.${deviation.status}`,
                        )}
                  </Tag>
                  <span
                    className={`${classes.subtle} ${classes.deadline}`}
                    data-state={deadlineState(deviation.dueDate, today)}
                  >
                    {deadlineText(deviation.dueDate, today, t, i18n.language)}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const CompletedPanel = ({
  items,
}: {
  items: ReturnType<typeof completedList>;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <section aria-label={t(`${K}.tab.completed`)} className={classes.card}>
      <Paragraph data-size="sm" className={classes.intro}>
        {t(`${K}.completedIntro`)}
      </Paragraph>
      {items.length === 0 ? (
        <Paragraph
          data-size="sm"
          className={`${classes.empty} ${layout.subtle}`}
        >
          {t(`${K}.empty.completed`)}
        </Paragraph>
      ) : (
        <ul className={classes.rows}>
          {items.map((item) => (
            <li key={item.key} className={classes.row}>
              <div className={classes.rowMain} data-icon="false">
                <div className={classes.rowText}>
                  <span className={classes.rowTitle}>{item.title}</span>
                  <span className={classes.subtle}>{item.when}</span>
                </div>
                <div className={classes.side}>
                  <Link asChild data-size="sm">
                    <RouterLink
                      to={item.link}
                      aria-label={t(`${K}.completed.seeAria`, {
                        title: item.title,
                      })}
                    >
                      {t(`${K}.completed.see`)}
                    </RouterLink>
                  </Link>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
